// Shared number, currency and date formatting for the Qantas deal trackers.
// Feeds mix numbers and strings ("$1,234.50", "120.00", 424), so every helper
// goes through toAmount first and returns an em dash for values it cannot read.
// The Pages Function in frontend/functions imports this module under Node ESM,
// so local imports keep their .js extension.
import { dealKeyFromUrl } from './dealShare.js';

const AU_LOCALE = 'en-AU';
const AU_TIME_ZONE = 'Australia/Melbourne';
const EMPTY = '—';

export const toAmount = (value) => {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const cleaned = String(value).trim().replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : null;
};

export const formatAud = (value, { decimals = 2 } = {}) => {
  const amount = toAmount(value);
  if (amount == null) return EMPTY;
  return new Intl.NumberFormat(AU_LOCALE, {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
};

export const formatRatio = (value, decimals = 2) => {
  const amount = toAmount(value);
  return amount == null ? EMPTY : amount.toFixed(decimals);
};

export const formatPoints = (value) => {
  const amount = toAmount(value);
  if (amount == null) return EMPTY;
  return new Intl.NumberFormat(AU_LOCALE, { maximumFractionDigits: 0 }).format(Math.round(amount));
};

// Accepts Date objects, ISO strings, unix seconds or unix milliseconds.
export const toDate = (input) => {
  if (input == null || input === '') return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;
  if (typeof input === 'number' || /^\d+(\.\d+)?$/.test(String(input).trim())) {
    const numeric = Number(input);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;
    return new Date(numeric < 1e12 ? numeric * 1000 : numeric);
  }
  const parsed = new Date(input);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDateAu = (input) => {
  const date = toDate(input);
  if (!date) return EMPTY;
  return new Intl.DateTimeFormat(AU_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: AU_TIME_ZONE,
  }).format(date);
};

export const formatDateTimeAu = (input) => {
  const date = toDate(input);
  if (!date) return EMPTY;
  return new Intl.DateTimeFormat(AU_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: AU_TIME_ZONE,
  }).format(date);
};

// "Sat 10 Oct" in Melbourne time, for recent-visit notes.
export const formatWeekdayDateAu = (input) => {
  const date = toDate(input);
  if (!date) return EMPTY;
  const parts = new Intl.DateTimeFormat(AU_LOCALE, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: AU_TIME_ZONE,
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value;
  return `${part('weekday')} ${part('day')} ${part('month')}`;
};

// Calendar days ("YYYY-MM-DD") are already Melbourne days, so they are read and
// formatted as plain dates (noon UTC) rather than converted between time zones.
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const calendarDateToUtc = (value) => {
  const match = CALENDAR_DATE.exec(String(value ?? ''));
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, 12));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? date : null;
};

// "14 Aug 2026", or "14 Aug" with { year: false }.
export const formatCalendarDate = (value, { year = true } = {}) => {
  const date = calendarDateToUtc(value);
  if (!date) return EMPTY;
  return new Intl.DateTimeFormat(AU_LOCALE, {
    day: 'numeric',
    month: 'short',
    ...(year ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  }).format(date);
};

// "Aug" for a calendar day.
export const formatCalendarMonth = (value) => {
  const date = calendarDateToUtc(value);
  return date ? new Intl.DateTimeFormat(AU_LOCALE, { month: 'short', timeZone: 'UTC' }).format(date) : EMPTY;
};

// Today's (or the given instant's) Melbourne calendar day as "YYYY-MM-DD".
export const melbourneCalendarDate = (input = Date.now()) => {
  const date = toDate(input);
  if (!date) return null;
  const parts = new Intl.DateTimeFormat(AU_LOCALE, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: AU_TIME_ZONE,
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
};

const plural = (count, unit) => `${count} ${unit}${count === 1 ? '' : 's'} ago`;

export const timeSince = (timestamp, now = Date.now()) => {
  const date = toDate(timestamp);
  if (!date) return '';
  const diffMs = Math.max(0, now - date.getTime());
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return plural(minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return plural(hours, 'hour');
  return plural(Math.floor(hours / 24), 'day');
};

export const getBasePointsRate = (membershipTier) => (membershipTier === 'standard' ? 1 : 3);

export const getMembershipDiscountRate = (membershipTier) => (membershipTier === 'pointsClubPlus' ? 0.1 : 0);

// The raw numbers behind every table row. The tables, the price history and the
// shared-link preview (frontend/functions) all use these, so a value is the same
// wherever it appears.
const computeMetrics = ({ price, bonusPoints, basePointsRate, includeBasePoints }) => {
  const basePoints = price * basePointsRate;
  const totalPoints = bonusPoints + basePoints;
  const effectivePoints = includeBasePoints ? totalPoints : bonusPoints;
  const pointsPerDollar = price ? effectivePoints / price : 0;
  const centsPerPoint = effectivePoints ? (price / effectivePoints) * 100 : 0;
  return { price, bonusPoints, basePoints, totalPoints, effectivePoints, pointsPerDollar, centsPerPoint };
};

const formatMetrics = (item, metrics) => ({
  ...item,
  price: formatAud(metrics.price),
  bonusPoints: formatPoints(metrics.bonusPoints),
  totalPoints: formatPoints(metrics.totalPoints),
  points: formatPoints(metrics.effectivePoints),
  pointsPerDollar: formatRatio(metrics.pointsPerDollar),
  centsPerPoint: formatRatio(metrics.centsPerPoint),
});

// Qantas Wine: Points Club tiers earn 3 base points per $1 (standard earns 1),
// Points Club Plus also takes 10% off, and the $50 voucher comes off the price.
// `wine.points` is the advertised bonus.
export const wineMetrics = (wine, membershipTier = 'standard', voucherAmount = 0, includeBasePoints = true) => {
  let price = ((toAmount(wine?.price) || 0) * (1 - getMembershipDiscountRate(membershipTier))) - voucherAmount;
  if (price < 0) price = 0;
  const bonusPoints = toAmount(wine?.points) || 0;
  return computeMetrics({ price, bonusPoints, basePointsRate: getBasePointsRate(membershipTier), includeBasePoints });
};

export const recalculateWineMetrics = (wines, membershipTier = 'standard', voucherAmount = 0, includeBasePoints = true) =>
  (wines || []).map((wine) => formatMetrics(wine, wineMetrics(wine, membershipTier, voucherAmount, includeBasePoints)));

export const MARKETPLACE_BASE_POINTS_RATE = 3;

// Qantas Marketplace: every member earns 3 base points per $1 on top of the bonus.
export const marketplaceMetrics = (product, includeBasePoints = true) => {
  let price = toAmount(product?.price) || 0;
  if (price < 0) price = 0;
  const bonusPoints = toAmount(product?.bonusPoints) || 0;
  return computeMetrics({ price, bonusPoints, basePointsRate: MARKETPLACE_BASE_POINTS_RATE, includeBasePoints });
};

export const recalculateMarketplaceMetrics = (products, includeBasePoints = true) =>
  (products || []).map((product) => formatMetrics(product, marketplaceMetrics(product, includeBasePoints)));

// Pack sizes a Qantas Wine name can state, checked in this order. A number taken
// from the name only counts when it is a real pack size (2 to 24 bottles).
const HALF_DOZEN = /\bhalf[\s-]*dozen\b/i;
const NAME_PACK_SIZES = [
  { pattern: HALF_DOZEN, size: 6 },
  { pattern: /\b(?:doz|dozen|twelve)\b/i, size: 12 },
  { pattern: /\b(\d{1,2})[\s-]*(?:pack|pk|bottles?)\b/i, size: Number }, // "3 pack", "3-pack", "6pk", "6 bottles"
  { pattern: /\b(\d{1,2})\s*x\s*\d+\s*ml\b/i, size: Number }, // "6 x 750ml"
  { pattern: /\bsix\b/i, size: 6 }, // "Premium Six", "Six for Entertaining"
  { pattern: /\b(?:three|triple)[\s-]*pack\b|\btrio\b/i, size: 3 },
  { pattern: /\bduo\b|\b(?:twin|two)[\s-]*pack\b|\bpair\b/i, size: 2 },
];
// Product keys such as WP10KRED12PK end in the pack size.
const KEY_PACK_SIZE = /(?:^|\D)(\d{1,2})PK$/i;
// MIX… keys are mixed packs whose size Qantas doesn't publish.
const MIXED_PACK_KEY = /^MIX/i;

const isPackSize = (count) => Number.isInteger(count) && count >= 2 && count <= 24;

const packSizeFromName = (name) => {
  const text = String(name || '');
  for (const { pattern, size } of NAME_PACK_SIZES) {
    const match = pattern.exec(text);
    const count = match && (typeof size === 'function' ? size(match[1]) : size);
    if (isPackSize(count)) return count;
  }
  return null;
};

const packSizeFromKey = (key) => {
  const match = KEY_PACK_SIZE.exec(key);
  const count = match ? Number(match[1]) : null;
  return isPackSize(count) ? count : null;
};

// Bottles in a Qantas Wine deal, or null when the pack size is unknown.
// The feed's numberOfBottles is Qantas' first case-variant label, 12 for names
// containing "dozen", and otherwise 1, even for packs whose size Qantas only
// states in the name ("Premium Six", "3 pack") or the product key
// ("WP10KRED12PK"). So a count above 1 is trusted; otherwise the name, then the
// key, decide, and a plain listing is a single bottle.
export const wineBottleCount = (wine) => {
  const raw = wine?.numberOfBottles;
  const listed = toAmount(raw);
  // The feed also says 12 for any name containing "dozen", "Half Dozen" included.
  if (Number.isInteger(listed) && listed > 1) return listed === 12 && HALF_DOZEN.test(String(wine?.name || '')) ? 6 : listed;

  const key = dealKeyFromUrl(wine?.url) || '';
  const inferred = packSizeFromName(wine?.name) ?? packSizeFromKey(key);
  if (inferred) return inferred;
  if (MIXED_PACK_KEY.test(key)) return null;

  const missing = raw == null || String(raw).trim() === '';
  return missing || listed === 1 ? 1 : null;
};

// "1 bottle", "6 bottles", or null for a count that isn't a positive whole number.
export const formatBottles = (count) => {
  const bottles = toAmount(count);
  if (!Number.isInteger(bottles) || bottles < 1) return null;
  return `${bottles} bottle${bottles === 1 ? '' : 's'}`;
};

// Price per bottle, or null when the price or the pack size is unknown. Works on
// raw feed items and on recalculated rows (whose price is "$1,234.50").
export const pricePerBottle = (wine) => {
  const price = toAmount(wine?.price);
  const bottles = wineBottleCount(wine);
  if (price == null || !bottles) return null;
  return price / bottles;
};

// Historical feeds store points per dollar only; derive cents per point from it.
export const centsPerPointFromPpd = (pointsPerDollar) => {
  const ppd = toAmount(pointsPerDollar);
  return ppd ? 100 / ppd : null;
};
